/**
 * DropdownGenerate Component
 * Feature 017: Instagram Reel Creation
 *
 * Dropdown menu for content generation options:
 * - Generate post (existing flow)
 * - Generate Instagram Reel (new feature)
 */

import { memo } from 'react';
import { Sparkles, FileText, Film, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import type { IDropdownGenerateProps } from '../../@types/reels';

/**
 * DropdownGenerate Component
 *
 * Displays a dropdown button with generation options:
 * - Generate post: Creates a social media post
 * - Generate Instagram Reel: Creates an Instagram Reel script
 */
const DropdownGenerate = ({
  onGeneratePost,
  onGenerateReel,
  disabled = false,
  isGenerating = false,
}: IDropdownGenerateProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled || isGenerating}>
        <Button
          variant="outline"
          className="bg-generate-btn-bg text-generate-btn border-generate-btn-border hover:bg-generate-btn-bg/80 hover:text-generate-btn shadow-none gap-2 rounded-lg h-9 px-4"
        >
          <Sparkles className="h-4 w-4" />
          Generate
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          onClick={onGeneratePost}
          disabled={disabled || isGenerating}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2" />
          <div className="flex flex-col">
            <span className="font-medium">Generate post</span>
            <span className="text-xs text-muted-foreground">Create a social media post</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onGenerateReel}
          disabled={disabled || isGenerating}
          className="cursor-pointer"
        >
          <Film className="h-4 w-4 mr-2" />
          <div className="flex flex-col">
            <span className="font-medium">Generate Instagram Reel</span>
            <span className="text-xs text-muted-foreground">Create a 15-30s reel script</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Memoized DropdownGenerate component
 */
export const DropdownGenerateMemo = memo(DropdownGenerate);
